import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, EmptyState, Tooltip } from '../../components';
import { getCourses, CourseDoc } from '../../services/course-service';
import { getMyEnrollments, EnrollmentDoc } from '../../services/enrollment-service';
import { getQuizAttempts, QuizAttempt } from '../../services/quiz-attempt-service';
import { PhaseContext } from '../../contexts/PhaseContext';
import { getPitch, PitchSubmissionDoc } from '../../services/pitch-submission-service';

const CATEGORIES = ['All', 'Business', 'Technical', 'Design', 'Finance', 'Presentation', 'Marketing'];

const CATEGORY_ICONS: Record<string, { icon: JSX.Element; color: string }> = {
  Technical: {
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
      </svg>
    ),
    color: 'bg-blue-500/15 text-blue-400',
  },
  Business: {
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.073a2.25 2.25 0 01-2.25 2.25h-13.5A2.25 2.25 0 012.25 18.223V14.15m17.25-4.5H4.5m0 0l.75-3.75m16.5 3.75l-.75-3.75M7.5 9.75V4.5A2.25 2.25 0 019.75 2.25h4.5A2.25 2.25 0 0116.5 4.5v5.25" />
      </svg>
    ),
    color: 'bg-purple-500/15 text-purple-400',
  },
  Marketing: {
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
      </svg>
    ),
    color: 'bg-orange-500/15 text-orange-400',
  },
  Design: {
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
      </svg>
    ),
    color: 'bg-pink-500/15 text-pink-400',
  },
  Finance: {
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
    color: 'bg-yellow-500/15 text-yellow-400',
  },
  Presentation: {
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
      </svg>
    ),
    color: 'bg-indigo-500/15 text-indigo-400',
  },
};

const DEFAULT_ICON = {
  icon: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  ),
  color: 'bg-chart/15 text-chart',
};

const priceLabel = (p?: string) => {
  if (!p || p.toLowerCase() === 'free') return 'Free';
  return p.startsWith('$') ? p : `$${p}`;
};

type Tab = 'coursework' | 'complementary';

const Courses = () => {
  const navigate = useNavigate();
  const phaseCtx = useContext(PhaseContext);
  const currentPhase = phaseCtx?.currentPhase ?? null;
  const phasesLoading = phaseCtx?.loading ?? false;
  const phases = phaseCtx?.phases ?? [];
  const isPhaseUnlocked = phaseCtx?.isPhaseUnlocked ?? (() => false);
  const allPhasesComplete = phaseCtx?.allPhasesComplete ?? false;

  const [courses,        setCourses]        = useState<CourseDoc[]>([]);
  const [enrollments,    setEnrollments]    = useState<EnrollmentDoc[]>([]);
  const [quizAttempts,   setQuizAttempts]   = useState<QuizAttempt[]>([]);
  const [isLoading,      setIsLoading]      = useState(true);
  const [activeTab,      setActiveTab]      = useState<Tab>('coursework');
  const [search,         setSearch]         = useState('');
  const [category,       setCategory]       = useState('All');
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [initialPitch,   setInitialPitch]   = useState<PitchSubmissionDoc | null>(null);
  const [finalPitch,     setFinalPitch]     = useState<PitchSubmissionDoc | null>(null);
  const [pitchLoading,   setPitchLoading]   = useState(true);

  useEffect(() => {
    setSelectedPhaseId(null);
  }, [currentPhase?.id]);

  useEffect(() => {
    Promise.all([getCourses(), getMyEnrollments(), getQuizAttempts()])
      .then(([c, e, qa]) => {
        setCourses(c.filter(course => course.status === 'published'));
        setEnrollments(e);
        setQuizAttempts(qa);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    getPitch('initial')
      .then(p => setInitialPitch(p))
      .catch(() => {})
      .finally(() => setPitchLoading(false));
  }, []);

  useEffect(() => {
    if (!allPhasesComplete) return;
    getPitch('final').then(p => setFinalPitch(p)).catch(() => {});
  }, [allPhasesComplete]);

  const enrollmentMap = new Map(enrollments.map(e => {
    const cid = typeof e.courseId === 'object' ? e.courseId._id : e.courseId;
    return [cid, e];
  }));

  // itemId → type, built from course modules so progress can exclude failed quizzes
  const itemTypeMap = new Map<string, string>();
  courses.forEach(course => {
    (course.modules || []).forEach(mod => {
      mod.items.forEach(item => { if (item._id) itemTypeMap.set(item._id, item.type); });
    });
  });

  // quiz itemIds the user has passed at least once
  const passedQuizItems = new Set(quizAttempts.filter(a => a.passed).map(a => a.itemId));

  const accessiblePhases = phases.filter(p => isPhaseUnlocked(p.id));
  const effectivePhaseId = selectedPhaseId ?? currentPhase?.id ?? null;
  const selectedPhase = phases.find(p => p.id === effectivePhaseId) ?? currentPhase;

  const courseworkCourses = courses
    .filter(c =>
      c.courseType === 'coursework' &&
      selectedPhase?.courseIds?.includes(c._id) &&
      c.title.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const ids = selectedPhase?.courseIds ?? [];
      return ids.indexOf(a._id) - ids.indexOf(b._id);
    });

  const complementaryCourses = courses.filter(c =>
    c.courseType === 'complementary' &&
    (category === 'All' || c.category === category) &&
    c.title.toLowerCase().includes(search.toLowerCase())
  );

  const displayCourses = activeTab === 'coursework' ? courseworkCourses : complementaryCourses;

  if (isLoading || phasesLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="section-gap">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-text">Learning</h1>
        <p className="text-muted mt-1">Your curriculum for this phase</p>
      </div>

      {/* Search */}
      <input
        value={search}
        onChange={e => { setSearch(e.target.value); }}
        placeholder="Search courses..."
        className="w-full px-4 py-2.5 bg-surface border border-line rounded-lg text-text text-sm placeholder:text-muted focus:outline-none focus:border-chart/50"
      />

      {/* Phase dropdown — shown when >1 accessible phase and on coursework tab */}
      {activeTab === 'coursework' && accessiblePhases.length > 1 && (
        <select
          value={effectivePhaseId ?? ''}
          onChange={e => setSelectedPhaseId(e.target.value || null)}
          className="w-full sm:w-auto px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none focus:border-chart/50"
        >
          {accessiblePhases.map(p => (
            <option key={p.id} value={p.id}>
              {p.title}{p.id === currentPhase?.id ? ' (Current)' : ' ✓'}
            </option>
          ))}
        </select>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-line">
        <Tooltip content="Courses compulsory to complete before advancing to the next phase">
          <button
            onClick={() => { setActiveTab('coursework'); setSearch(''); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === 'coursework'
                ? 'border-chart text-chart'
                : 'border-transparent text-muted hover:text-text'
            }`}
          >
            Coursework
          </button>
        </Tooltip>
        <Tooltip content="Optional courses you can purchase to supplement your learning">
          <button
            onClick={() => { setActiveTab('complementary'); setSearch(''); setCategory('All'); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === 'complementary'
                ? 'border-chart text-chart'
                : 'border-transparent text-muted hover:text-text'
            }`}
          >
            Complementary
          </button>
        </Tooltip>
      </div>

      {/* Complementary category filters */}
      {activeTab === 'complementary' && (
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map(cat => (
            <Button
              key={cat}
              variant={category === cat ? 'primary' : 'subtle'}
              size="sm"
              onClick={() => setCategory(cat)}
            >
              {cat}
            </Button>
          ))}
        </div>
      )}

      {/* Phase 0 + Final Pitch cards — always visible in coursework tab */}
      {activeTab === 'coursework' && !pitchLoading && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4 p-4 bg-shell border border-line rounded-card">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium bg-warning/15 text-warning px-2 py-0.5 rounded-full">Phase 0</span>
                {initialPitch?.isSubmitted && (
                  <span className="text-xs font-medium bg-success/15 text-success px-2 py-0.5 rounded-full">Submitted ✓</span>
                )}
              </div>
              <p className="text-sm font-medium text-text">Initial Pitch</p>
              <p className="text-xs text-muted mt-0.5">
                {initialPitch?.isSubmitted
                  ? 'Submitted and preserved as a record.'
                  : 'Required before the midpoint phase. All fields optional.'}
              </p>
            </div>
            <Link to="/app/pitch/initial">
              <Button variant={initialPitch?.isSubmitted ? 'subtle' : 'primary'} size="sm">
                {initialPitch?.isSubmitted ? 'View' : 'Fill Out'}
              </Button>
            </Link>
          </div>

          {allPhasesComplete && (
            <div className="flex items-center justify-between gap-4 p-4 bg-shell border border-chart/30 rounded-card">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium bg-chart/15 text-chart px-2 py-0.5 rounded-full">Final Pitch</span>
                  {finalPitch?.reviewStatus && finalPitch.reviewStatus !== 'pending' && (
                    <span className="text-xs font-medium bg-success/15 text-success px-2 py-0.5 rounded-full">Graded ✓</span>
                  )}
                </div>
                <p className="text-sm font-medium text-text">Final Pitch Submission</p>
                <p className="text-xs text-muted mt-0.5">
                  {finalPitch?.isSubmitted
                    ? 'Submitted for mentor review.'
                    : 'All phases complete. Submit your investor-ready final pitch.'}
                </p>
              </div>
              <Link to="/app/pitch/final">
                <Button variant={finalPitch?.isSubmitted ? 'subtle' : 'primary'} size="sm">
                  {finalPitch?.isSubmitted ? 'View' : 'Open'}
                </Button>
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Course Grid */}
      {activeTab === 'coursework' && !selectedPhase ? (
        <EmptyState
          title="No active phase"
          description="Your journey phases are loading or haven't started yet."
        />
      ) : displayCourses.length === 0 ? (
        <EmptyState
          title={activeTab === 'coursework' ? 'No courses in this phase' : 'No complementary courses found'}
          description={
            activeTab === 'coursework'
              ? 'No courses have been assigned to your current phase yet.'
              : 'Try adjusting your search or category filter.'
          }
          action={
            activeTab === 'complementary' && (search || category !== 'All') ? (
              <Button variant="primary" onClick={() => { setSearch(''); setCategory('All'); }}>Clear Filters</Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayCourses.map(course => {
            const cat = CATEGORY_ICONS[course.category] ?? DEFAULT_ICON;
            return (
              <div
                key={course._id}
                onClick={() => navigate(`/app/lms/courses/${course._id}`)}
                className="p-5 border border-line rounded-xl hover:border-chart/30 hover:bg-surface/30 transition-all duration-200 cursor-pointer group"
              >
                {/* Thumbnail */}
                <div className={`w-full h-32 rounded-lg mb-4 flex items-center justify-center overflow-hidden ${cat.color}`}>
                  {course.thumbnailUrl ? (
                    <img src={course.thumbnailUrl} alt={course.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-10 h-10">{cat.icon}</div>
                  )}
                </div>

                {/* Meta */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-chart/15 text-chart px-2 py-0.5 rounded">{course.category}</span>
                  <span className="text-xs text-muted capitalize">{course.level}</span>
                </div>

                <h3 className="text-text font-semibold mb-1 group-hover:text-chart transition-colors line-clamp-2">
                  {course.title}
                </h3>

                {activeTab === 'complementary' && course.description && (
                  <p className="text-muted text-sm line-clamp-2 mb-2">{course.description}</p>
                )}

                <div className="text-xs text-muted">
                  {(course.modules || []).length} module{(course.modules || []).length !== 1 ? 's' : ''}
                </div>

                {/* Progress — coursework only */}
                {activeTab === 'coursework' && (() => {
                  const en = enrollmentMap.get(course._id);
                  if (!en) return null;
                  const total = (course.modules || []).reduce((s, m) => s + m.items.length, 0);
                  const effectiveCompleted = en.completedItems.filter(iid =>
                    itemTypeMap.get(iid) === 'quiz' ? passedQuizItems.has(iid) : true,
                  ).length;
                  const pct = total > 0 ? Math.round((effectiveCompleted / total) * 100) : 0;
                  return (
                    <div className="mt-3 pt-3 border-t border-line">
                      <div className="flex justify-between text-xs text-muted mb-1">
                        <span>Progress</span><span>{pct}%</span>
                      </div>
                      <div className="w-full bg-surface rounded-full h-1">
                        <div className="bg-chart h-1 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })()}

                {/* Price — complementary only */}
                {activeTab === 'complementary' && (
                  <div className="mt-3 pt-3 border-t border-line">
                    <span className={`text-sm font-semibold ${!course.price || course.price.toLowerCase() === 'free' ? 'text-success' : 'text-chart'}`}>
                      {priceLabel(course.price)}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Courses;
